import { Link2 } from "lucide-react";
import React from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel
} from "react-accessible-accordion";
import UserIcon from "../../../../../../assets/icons/icon-user.png";
import { BookableArea } from "../../../../../../types/venues";
import { EDSBadge } from "../../../../../eds/EDSBadge";
import { AreaData, ZoneData } from "./FindAreasController";

type FindAreasTabContentType = {
  multiSelect?: boolean;
  zones: ZoneData[];
  selected: BookableArea[];
  setSelected: (selected: BookableArea[]) => void;
  bookableAreas?: BookableArea[];
};

const FindAreasTabContent = ({
  multiSelect,
  zones,
  selected,
  setSelected,
  bookableAreas = []
}: FindAreasTabContentType) => {
  const selectedIds = selected.map((area) => area.id);

  const resolveCombinationAreas = (area: AreaData): BookableArea[] => {
    if (!area.areaIds) return [area];
    return area.areaIds.map((id) => {
      const foundArea = bookableAreas.find((a) => a.id === id);
      return {
        id,
        name: foundArea?.name ?? id,
        min_people: foundArea?.min_people ?? area.min_people,
        max_people: foundArea?.max_people ?? area.max_people,
        max_people_seated:
          foundArea?.max_people_seated ?? area.max_people_seated,
        zone: area.zone
      };
    });
  };

  const isCombinationSelected = (area: AreaData): boolean => {
    if (!area.areaIds) return selectedIds.includes(area.id);
    return area.areaIds.every((id) => selectedIds.includes(id));
  };

  const setSelection = (selectedArea: AreaData) => {
    const resolvedAreas = resolveCombinationAreas(selectedArea);

    if (!multiSelect) {
      setSelected(resolvedAreas);
      return;
    }

    if (isCombinationSelected(selectedArea)) {
      const idsToRemove = new Set(resolvedAreas.map((a) => a.id));
      setSelected(selected.filter((a) => !idsToRemove.has(a.id)));
    } else {
      const existingIds = new Set(selectedIds);
      const newAreas = resolvedAreas.filter((a) => !existingIds.has(a.id));
      setSelected([...selected, ...newAreas]);
    }
  };

  if (zones.length === 0) {
    return <div>No areas found.</div>;
  }

  return (
    <Accordion
      allowMultipleExpanded
      allowZeroExpanded
      preExpanded={Array.from(Array(zones.length).keys())}
    >
      {zones.map((bookingZone, zoneIndex) => (
        <div
          key={bookingZone.name ?? `zone-${zoneIndex}`}
          className="zone-section mb-6 last:mb-0"
        >
          <AccordionItem
            key={bookingZone.name ?? `zone-${zoneIndex}`}
            uuid={zoneIndex}
          >
            <AccordionItemHeading>
              <AccordionItemButton className="accordion__button accordion__button_pop bg-transparent border-none py-[5px] px-0 hover:bg-transparent">
                <div className="AreaCount text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-[7px] mb-3">
                  {`${bookingZone.name} (${bookingZone.areas.length})`}
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <div className="Tab1__Content grid grid-cols-2 gap-3">
                {bookingZone.areas.map((bookingArea) => (
                  <button
                    type="button"
                    className={[
                      "AreaTable bg-card border border-border text-left text-foreground py-3.5 px-4 capitalize",
                      "flex flex-col justify-start cursor-pointer transition-all duration-150 ease-in-out",
                      "hover:bg-accent/50 hover:border-primary/60",
                      isCombinationSelected(bookingArea) ? "SelectedTable" : "",
                      bookingArea.occupied && !multiSelect
                        ? "occupied opacity-60 cursor-not-allowed pointer-events-none"
                        : ""
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={{ borderRadius: "var(--radius)" }}
                    key={bookingArea.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!bookingArea.occupied || multiSelect) {
                        setSelection(bookingArea);
                      }
                    }}
                    disabled={bookingArea.occupied && !multiSelect}
                    aria-disabled={bookingArea.occupied && !multiSelect}
                  >
                    <div className="AreaTableContent flex justify-between items-center mb-2">
                      <div
                        className="AreaName font-semibold text-sm text-foreground leading-tight flex items-center gap-1.5"
                        style={{ fontFamily: "var(--font-family)" }}
                      >
                        {bookingArea.combination ? (
                          <Link2
                            className="w-3.5 h-3.5 shrink-0 text-muted-foreground"
                            aria-label="Linked tables"
                          />
                        ) : null}
                        <span>{bookingArea.name}</span>
                      </div>
                      {bookingArea.occupied ? (
                        <EDSBadge
                          variant="default"
                          className="occupied-badge"
                          style={
                            {
                              "--booking-type-color":
                                "var(--occupiedBackground)",
                              "--booking-type-foreground": "var(--black)"
                            } as React.CSSProperties
                          }
                        >
                          <span className="occupied-badge-text">Occupied</span>
                        </EDSBadge>
                      ) : null}
                    </div>
                    <div className="AreaTableContentGuestCount flex items-center gap-1 text-sm text-muted-foreground">
                      <span className="AreaGuestCount inline-flex items-center gap-1">
                        <img
                          src={UserIcon}
                          className="AreaUserIcon w-4 h-4 object-contain shrink-0"
                          alt="icon-user"
                        />{" "}
                        {`${bookingArea.min_people ?? ""}-${
                          bookingArea.max_people ?? ""
                        }`}
                      </span>
                    </div>
                    {bookingArea.occupied ? (
                      <div className="Occupied__Time text-xs font-medium text-primary normal-case mt-1.5 leading-tight">
                        Free at {bookingArea.occupiedAreaEndTime}
                      </div>
                    ) : null}
                  </button>
                ))}
              </div>
            </AccordionItemPanel>
          </AccordionItem>
        </div>
      ))}
    </Accordion>
  );
};

export default React.memo<FindAreasTabContentType>(FindAreasTabContent);
